import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';
import { OrderProcessingService } from 'src/app/services/portal-supplier/order-processing.service';
import { ProcessingOrders } from 'src/app/models/supplier-portal/supplier/supplierOrdersProcessing.models';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import { map } from 'rxjs';

type PanelView = 'lots' | 'add-step1' | 'add-step2' | 'add-step3' | 'view-lot';

@Component({
  selector: 'app-fornitori-ordini-dettagli',
  standalone: false,
  templateUrl: './fornitori-ordini-dettagli.component.html',
  styleUrls: ['./fornitori-ordini-dettagli.component.scss'],
})


export class FornitoriOrdiniDettagliComponent implements OnInit {

  @ViewChild('certFileInput')
  private certFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('ddtFileInput')
  private ddtFileInput!: ElementRef<HTMLInputElement>;

  public readonly orderId: number;
  public readonly gammaId: number;
  public readonly certificatoId: number;
  public readonly isStoricoRoute: boolean;
  public order: FornitoriAdmin.OrderDetail | null = null;
  public loading = false;
  public saving = false;

  public selectedProduct: FornitoriAdmin.IOrderDetailProduct | null = null;
  public selectedBatch: FornitoriAdmin.IOrderDetailBatch | null = null;
  public panelView: PanelView = 'lots';
  public certsExpanded = true;
  public savingDdt = false;

  public newBatch = {
    batch_year: new Date().getFullYear(),
    batch_week: 1,
    qta: 0,
    serial_from: 0,
    serial_to: 0,
  };
  public pendingBatchId = 0;

  // ── Certificate validation (Quality Certifier) ─────────────────────
  public certToValidate: FornitoriAdmin.IOrderDetailDocument | null = null;
  public certToView: FornitoriAdmin.IOrderDetailDocument | null = null;
  public validatingCert = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly svc: FornitoriAdminMainService,
    private readonly orderServ: OrderProcessingService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    // OpenModalService — usato per il toggle del product-detail-modal (R.2).
    public readonly closeSrv: OpenModalService,
  ) {
    this.isStoricoRoute = this.route.snapshot.url[0]?.path === 'storico';
    this.orderId = +(this.route.snapshot.paramMap.get('ordineId') ?? '0');
    this.gammaId = +(this.route.snapshot.paramMap.get('gammaId') ?? '0');
    this.certificatoId = +(
      this.route.snapshot.paramMap.get('certificatoId') ?? '0'
    );
  }

  ngOnInit(): void {
    this.loadDetail();
  }

  get isStorico(): boolean {
    return !!this.order?.or_data_chiusura;
  }

  get hasDdt(): boolean {
    return !!this.order?.ddt_url;
  }

  get allPrepared(): boolean {
    return (
      (this.order?.products?.length ?? 0) > 0 &&
      (this.order?.products?.every((p) => p.is_prepared) ?? false)
    );
  }

  get products(): FornitoriAdmin.IOrderDetailProduct[] {
    return this.order?.products ?? [];
  }

  loadDetail(): void {
    if (this.certificatoId == null) return;
    this.loading = true;

    const source$: import('rxjs').Observable<FornitoriAdmin.OrderDetail> =
      this.isStoricoRoute
        ? this.svc.getSupplierCloseOrdersDetails(
            this.orderId,
            this.gammaId,
            this.certificatoId,
          )
        : this.orderId || this.gammaId
          ? this.svc.getSupplierOpenOrdersDetails(
              this.orderId,
              this.gammaId,
              this.certificatoId,
            )
          : this.orderServ
              .getCertificationByID(this.certificatoId)
              .pipe(
                map(
                  (cert: ProcessingOrders.Certification) =>
                    new FornitoriAdmin.OrderDetail(cert as any),
                ),
              );

    source$.subscribe({
      next: (data) => {
        this.order = data;
        if (this.selectedProduct) {
          this.selectedProduct =
            data.products.find(
              (p) =>
                p.supplier_certificate_product_id ===
                this.selectedProduct!.supplier_certificate_product_id,
            ) ?? null;
          if (this.selectedBatch && this.selectedProduct) {
            this.selectedBatch =
              this.selectedProduct.batches.find(
                (b) =>
                  b.supplier_certificate_batch_id ===
                  this.selectedBatch!.supplier_certificate_batch_id,
              ) ?? null;
          }
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  selectProduct(p: FornitoriAdmin.IOrderDetailProduct): void {
    if (
      this.selectedProduct?.supplier_certificate_product_id ===
      p.supplier_certificate_product_id
    ) {
      this.selectedProduct = null;
      return;
    }
    this.selectedProduct = p;
    this.panelView = 'lots';
    this.selectedBatch = null;
  }

  // ──────────────────────────────────────────────────────────────────
  // Product detail modal (v2.12.1 R.2)
  // ──────────────────────────────────────────────────────────────────

  /**
   * 🚨 FIX v2.12.1 (R.2): pre-v2.12.1 le righe dei prodotti avevano
   * hover cursor:pointer ma click no-op per i prodotti SENZA
   * `fornitore_certificato_prodotti_id` (i certificati ancora da
   * generare). UX ingannevole. Ora cliccando si apre il modal
   * `<app-product-detail-modal>` (pattern B.3 v2.7.0) con le info
   * complete del prodotto (descrizione, prezzo, peso, scorta, posizione
   * magazzino, ecc.). Per i prodotti CON certificato il click resta
   * sul flow esistente `selectProduct(p)` (apre i lotti).
   *
   * Apertura/chiusura via `closeSrv.prodDetOn()`/`prodDetOff()` —
   * stesso pattern usato da confirm-products (v2.11.1 fix #1).
   * Il close button interno del modal chiama `prodDetOff()`
   * automaticamente.
   */
  public openedProductCode: string | null = null;

  openProductDetail(code: string | null | undefined): void {
    if (!code) return;
    this.openedProductCode = code.trim();
    this.closeSrv.prodDetOn();
  }

  closePanel(): void {
    this.selectedProduct = null;
    this.selectedBatch = null;
    this.panelView = 'lots';
  }

  startAddLot(): void {
    this.newBatch = {
      batch_year: new Date().getFullYear(),
      batch_week: 1,
      qta: 0,
      serial_from: 0,
      serial_to: 0,
    };
    this.pendingBatchId = 0;
    this.panelView = 'add-step1';
  }

  submitStep1(): void {
    if (!this.selectedProduct) return;
    if (
      !this.newBatch.batch_week ||
      this.newBatch.batch_week < 1 ||
      this.newBatch.batch_week > 53
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'Attenzione',
        text: 'Inserisci una settimana valida (1-53).',
      });
      return;
    }
    if (this.newBatch.qta <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Attenzione',
        text: 'La quantità deve essere maggiore di zero.',
      });
      return;
    }
    if (this.newBatch.serial_from > this.newBatch.serial_to) {
      Swal.fire({
        icon: 'warning',
        title: 'Attenzione',
        text: 'Il range seriali non è valido.',
      });
      return;
    }
    this.saving = true;
    console.log('PRODUCT IS', this.selectedProduct);
    const payload: ProcessingOrders.IBatchToAdd = {
      certification_product_id:
        this.selectedProduct.fornitore_certificato_prodotti_id,
      supplier_certificate_batch_id: this.pendingBatchId,
      certification_batch_id: this.pendingBatchId,
      batch_year: this.newBatch.batch_year,
      batch_week: this.newBatch.batch_week,
      serial_from: this.newBatch.serial_from,
      serial_to: this.newBatch.serial_to,
      qta: this.newBatch.qta,
    };

    this.orderServ.putCertificationBatch(payload).subscribe({
      next: (res: any) => {
        this.ngZone.run(() => {
          this.saving = false;
          const ok = !('success' in res) || res.success === true;
          if (!ok) {
            const errText: string =
              res?.error_message?.msg_text ||
              res?.error_message?.msg_type ||
              "Errore durante l'aggiunta del lotto.";
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text: errText,
            });
            return;
          }
          const batchId =
            res?.data?.result_id ?? res?.supplier_certificate_batch_id ?? 0;
          if (batchId) this.pendingBatchId = batchId;
          this.loadDetail();
          Swal.fire({
            icon: 'success',
            title: 'Lotto salvato!',
            timer: 1200,
            showConfirmButton: false,
            timerProgressBar: true,
          }).then(() => {
            this.panelView = 'add-step2';
            this.cdr.detectChanges();
          });
        });
      },
      error: () => {
        this.saving = false;
        Swal.fire({
          icon: 'error',
          title: 'Errore',
          text: "Errore durante l'aggiunta del lotto.",
        });
      },
    });
  }

  triggerCertUpload(): void {
    this.certFileInput?.nativeElement?.click();
  }

  onCertSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.pendingBatchId) {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Lotto non valido. Salva il lotto prima di caricare un certificato.',
      });
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result as string).split(',')[1] ?? '';
      const payload: ProcessingOrders.IFile = {
        certification_batch_id: this.pendingBatchId,
        certification_file: b64,
        certification_title: file.name,
        certification_file_name: file.name,
      };
      this.saving = true;
      this.orderServ.putCertificationFile(payload).subscribe({
        next: (res: any) => {
          this.ngZone.run(() => {
            this.saving = false;
            // Backend can return HTTP 200 with `{success:false,
            // error_message}` — see putCertificationBatch above for
            // the same pattern. Without the check we'd show "OK!" on
            // a failed write and lose data.
            const ok = !('success' in (res ?? {})) || res?.success === true;
            if (!ok) {
              Swal.fire({
                icon: 'error',
                title: 'Errore',
                text:
                  res?.error_message?.msg_text ||
                  'Errore durante il caricamento del certificato.',
              });
              return;
            }
            this.loadDetail();
            Swal.fire({
              icon: 'success',
              title: 'Certificato caricato!',
              timer: 1200,
              showConfirmButton: false,
              timerProgressBar: true,
            }).then(() => {
              this.panelView = 'add-step3';
              this.cdr.detectChanges();
            });
          });
        },
        error: () => {
          this.saving = false;
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Errore durante il caricamento del certificato.',
          });
        },
      });
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  getBatchDocs(): FornitoriAdmin.IOrderDetailDocument[] {
    if (!this.selectedProduct || !this.pendingBatchId) return [];
    const batch = this.selectedProduct.batches.find(
      (b) => b.supplier_certificate_batch_id === this.pendingBatchId,
    );
    return batch?.documents ?? [];
  }

  deleteCertFile(docId: number): void {
    Swal.fire({
      title: 'Eliminare il certificato?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '✔ SI',
      cancelButtonText: '✖ NO',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#dc2626',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.orderServ.deleteCertificationFile(docId).subscribe({
        next: (resp: any) => {
          // Same `success` check as the upload paths — backend
          // returns HTTP 200 with `{success:false}` on validation
          // errors. Without the check we'd silently reload a list
          // that still shows the cert.
          const ok = !('success' in (resp ?? {})) || resp?.success === true;
          if (!ok) {
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text:
                resp?.error_message?.msg_text ||
                'Errore durante la rimozione.',
            });
            return;
          }
          this.loadDetail();
        },
        error: () =>
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Errore durante la rimozione.',
          }),
      });
    });
  }

  addAnotherCert(): void {
    this.panelView = 'add-step2';
  }

  startAddCertForBatch(b: FornitoriAdmin.IOrderDetailBatch): void {
    this.pendingBatchId = b.supplier_certificate_batch_id;
    this.panelView = 'add-step2';
  }

  saveLotDone(): void {
    this.pendingBatchId = 0;
    this.panelView = 'lots';
    this.loadDetail();
  }

  viewLot(b: FornitoriAdmin.IOrderDetailBatch): void {
    this.selectedBatch = b;
    this.panelView = 'view-lot';
  }

  backToLots(): void {
    this.panelView = 'lots';
    this.selectedBatch = null;
  }

  editLot(b: FornitoriAdmin.IOrderDetailBatch): void {
    this.newBatch = {
      batch_year: b.batch_year,
      batch_week: b.batch_week,
      qta: b.qta,
      serial_from: b.serial_from,
      serial_to: b.serial_to,
    };
    this.pendingBatchId = b.supplier_certificate_batch_id;
    this.panelView = 'add-step1';
  }

  toggleCerts(): void {
    this.certsExpanded = !this.certsExpanded;
  }

  openDdtUpload(): void {
    this.ddtFileInput?.nativeElement?.click();
  }

  onDdtFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    // Guard: a missing `supplier_certificate_id` would silently make
    // the backend write the DDT against id=0 (or reject with a `200
    // OK { success:false }` body) — both look like "saved" client-side
    // but no PDF is ever persisted. Bail out fast before posting.
    const certId = this.order?.supplier_certificate_id ?? 0;
    if (!certId) {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Ordine non caricato correttamente. Ricarica la pagina.',
      });
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result as string).split(',')[1] ?? '';
      const payload: ProcessingOrders.IDDT = {
        certification_id: certId,
        ddt_file: b64,
        ddt_file_name: file.name,
      };
      this.savingDdt = true;
      this.orderServ.putCertificationDDT(payload).subscribe({
        next: (res: any) => {
          this.savingDdt = false;
          // Backend returns HTTP 200 with `{success:false,
          // error_message}` on validation failures — without this
          // check the previous code showed "DDT caricato!" but the
          // PDF wasn't actually persisted, and on reload of the
          // order the DDT was nowhere to be found. (User report
          // 2026-06-09.)
          const ok = !('success' in (res ?? {})) || res?.success === true;
          if (!ok) {
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text:
                res?.error_message?.msg_text ||
                'Errore durante il caricamento del DDT.',
            });
            this.cdr.detectChanges();
            return;
          }
          Swal.fire({
            icon: 'success',
            title: 'DDT caricato!',
            timer: 1500,
            showConfirmButton: false,
            timerProgressBar: true,
          });
          this.loadDetail();
          this.cdr.detectChanges();
        },
        error: () => {
          this.savingDdt = false;
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Errore durante il caricamento del DDT.',
          });
          this.cdr.detectChanges();
        },
      });
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  deleteDdt(): void {
    Swal.fire({
      title: 'Eliminare il DDT?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '✔ SI',
      cancelButtonText: '✖ NO',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#dc2626',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.orderServ
        .deleteCertificationDDT(this.order?.supplier_certificate_id ?? 0)
        .subscribe({
          next: (resp: any) => {
            // Backend may return HTTP 200 + `{success:false}` on
            // validation issues — same family of bug as the DDT
            // upload check above.
            const ok = !('success' in (resp ?? {})) || resp?.success === true;
            if (!ok) {
              Swal.fire({
                icon: 'error',
                title: 'Errore',
                text:
                  resp?.error_message?.msg_text ||
                  'Errore durante la rimozione del DDT.',
              });
              return;
            }
            Swal.fire({
              icon: 'success',
              title: 'DDT eliminato!',
              timer: 1500,
              showConfirmButton: false,
              timerProgressBar: true,
            });
            this.loadDetail();
          },
          error: () =>
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text: 'Errore durante la rimozione del DDT.',
            }),
        });
    });
  }

  confirmOrder(): void {
    Swal.fire({
      title: "Confermare l'ordine?",
      text: "L'ordine verrà chiuso e non sarà più modificabile.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '✔ CONFERMA',
      cancelButtonText: '✖ ANNULLA',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#dc2626',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.saving = true;
      this.orderServ
        .closeCertification(this.order?.supplier_certificate_id ?? 0)
        .subscribe({
          next: () => {
            this.saving = false;
            Swal.fire({
              icon: 'success',
              title: 'Ordine confermato!',
              timer: 1500,
              showConfirmButton: false,
              timerProgressBar: true,
            });
            this.router.navigate(['/fornitori-ordini']);
            this.cdr.detectChanges();
          },
          error: () => {
            this.saving = false;
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text: 'Errore durante la conferma.',
            });
            this.cdr.detectChanges();
          },
        });
    });
  }

  goBack(): void {
    this.router.navigate(['/fornitori-ordini']);
  }

  // ── Certificate validation (Quality Certifier) ─────────────────────
  // Spec (meeting Mirko/Massi 2026-07): VALIDATE button per cert →
  // confirm dialog ("Confermi che il certificato è valido?") → YES
  // saves quality certifier + date/time server-side (POST
  // validate_certification_file, check:"S") and the row shows a green
  // checkmark; NO just closes, no changes. Clicking an already
  // validated cert's checkmark shows who/when via a separate info modal.

  openValidateConfirm(doc: FornitoriAdmin.IOrderDetailDocument): void {
    this.certToValidate = doc;
  }

  cancelValidate(): void {
    this.certToValidate = null;
  }

  confirmValidate(): void {
    if (!this.certToValidate || this.validatingCert) return;
    const docId = this.certToValidate.supplier_certificate_document_id;
    this.validatingCert = true;
    this.orderServ.validateCertificationFile(docId, 'S').subscribe({
      next: (res: any) => {
        this.ngZone.run(() => {
          this.validatingCert = false;
          this.certToValidate = null;
          // Stesso pattern `{success:false}` su HTTP 200 usato dagli
          // altri endpoint di questo componente (vedi deleteCertFile,
          // onDdtFileSelected, ecc.).
          const ok = !('success' in (res ?? {})) || res?.success === true;
          if (!ok) {
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text:
                res?.error_message?.msg_text ||
                'Errore durante la validazione del certificato.',
            });
            return;
          }
          this.loadDetail();
        });
      },
      error: () => {
        this.validatingCert = false;
        this.certToValidate = null;
        Swal.fire({
          icon: 'error',
          title: 'Errore',
          text: 'Errore durante la validazione del certificato.',
        });
      },
    });
  }

  openCertInfo(doc: FornitoriAdmin.IOrderDetailDocument): void {
    this.certToView = doc;
  }

  closeCertInfo(): void {
    this.certToView = null;
  }

  formatCertCheckDate(val: string | null | undefined): string {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `in data ${dd}/${mm}/${yy} alle ore ${hh}:${min}`;
  }

}
